import { safeSetHTML } from './security.js';

/*
 * narrativeManager.js
 * Manages episodic content and story intros based on context.
 * REFACTORED: Restored static screen functionality with Smart Image Fallback
 */

const NarrativeManager = {
    autoPlayTimer: null,

    // Data for intros based on Context keywords found in user selection or problem data
    narratives: {
        "PLAYA": {
            title: "Misterio en la Playa",
            intro: "¡Hola Detective! Hemos encontrado huellas extrañas en la arena. Necesitamos tu ayuda para contar las conchas y resolver el misterio.",
            icon: "🏖️"
        },
        "GRANJA": {
            title: "Lío en la Granja",
            intro: "¡La vaca Lola se ha comido los números del granero! Ayuda al granjero a contar las manzanas antes de que anochezca.",
            icon: "🚜"
        },
        "SUPERMERCADO": {
            title: "Misión de Compras",
            intro: "El cajero automático se ha estropeado. Tienes que calcular el cambio exacto para que la gente pueda comprar su comida.",
            icon: "🛒"
        },
        "COLEGIO": {
            title: "Aventura Escolar",
            intro: "¡Han desaparecido los lápices de colores de la clase! Resuelve estos problemas para encontrar pistas sobre dónde están.",
            icon: "🏫"
        },
        "PARQUE": {
            title: "Tarde de Juegos",
            intro: "En el parque hay muchos juegos, pero necesitamos saber cuántos niños pueden subir al tobogán. ¡Investiga!",
            icon: "🛝"
        }
    },

    // Check if we should show an intro for the given context
    getContextIntro(contextKey) {
        // Try to find a matching key (partial match)
        const key = Object.keys(this.narratives).find(k => contextKey && contextKey.toUpperCase().includes(k));

        if (key) {
            return this.narratives[key];
        }

        // Default genérico if no specific context found
        return {
            title: "Nueva Misión",
            intro: "¡Un nuevo caso ha llegado a la agencia! Prepara tu lupa y tus superpoderes matemáticos.",
            icon: "🕵️"
        };
    },

    // Show the intro using the existing screen in index.html (RESTORED FUNCTIONALITY)
    async showIntro(contextKey) {
        console.log(`📖 Showing Narrative Intro for context: ${contextKey}`);

        // 1. Identify Elements
        const img = document.getElementById('narrativeImage');
        const textContainer = document.getElementById('narrativeText');
        const startBtn = document.getElementById('startAdventureBtn');
        const audioHeader = document.querySelector('.narrative-audio-header');

        // 2. Resolve Content
        const lang = window.currentLanguage || 'es-ES';
        let narrativeText = "";

        // Try Global Config first (Rich Text from src/config/narratives.js)
        if (window.CONTEXT_NARRATIVES && window.CONTEXT_NARRATIVES[contextKey]) {
            narrativeText = window.CONTEXT_NARRATIVES[contextKey][lang] || window.CONTEXT_NARRATIVES[contextKey]['es-ES'];
        }
        // Try local fallback
        else {
            const local = this.getContextIntro(contextKey);
            narrativeText = local.intro;
        }

        // Resolve Context Meta (Title & Icon)
        let contextTitle = "";
        let contextIcon = "";
        let contextColor = "#3b82f6"; // Default blue

        if (window.CONTEXTS) {
            const ctx = window.CONTEXTS.find(c => c.id === contextKey);
            if (ctx) {
                contextTitle = typeof ctx?.title === 'string' ? ctx.title : (ctx?.title?.[lang] || ctx?.title?.['es-ES'] || '');
                contextIcon = ctx.icon;
                if (ctx.color) contextColor = ctx.color;
            }
        }

        // Fallback for title/icon if still empty
        if (!contextTitle) {
            const local = this.getContextIntro(contextKey);
            contextTitle = local.title;
            contextIcon = local.icon;
        }

        // 3. Populate DOM Text
        if (textContainer) {
            // Using safeSetHTML to inject rich text structure
            safeSetHTML(textContainer, `
                <div style="text-align: center;">
                    <h2 style="color: ${contextColor}; margin-bottom: 1.5rem; font-size: 2.2rem; display: flex; align-items: center; justify-content: center; gap: 1rem;">
                        <span style="font-size: 1.2em; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));">${contextIcon}</span>
                        ${contextTitle}
                    </h2>
                    <div style="background: rgba(255,255,255,0.7); padding: 1.5rem; border-radius: 16px; border-left: 6px solid ${contextColor}; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                        <p style="font-size: 1.4rem; line-height: 1.6; color: #334155; font-family: 'Baloo 2', cursive; text-align: left;">
                            ${narrativeText}
                        </p>
                    </div>
                </div>
            `);
        }

        // 4. Handle Center Image with Smart Fallback
        if (img) {
            // Priority 1: PNG (Final Artwork)
            // Priority 2: SVG (Placeholder/Vector)
            const basePath = `assets/img/contexts/intro_${contextKey}`;

            img.style.display = 'block';
            img.style.border = `4px solid ${contextColor}`;
            img.style.borderRadius = '12px';
            img.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';

            // Set initial source to PNG
            img.src = `${basePath}.png`;

            // Smart Fallback: PNG -> SVG -> Inline Emoji Fallback -> Hide
            img.onerror = function () {
                // If .png fails, try .svg
                if (this.src && this.src.indexOf('.png') !== -1) {
                    console.log(`⚠️ PNG not found for ${contextKey}, trying SVG fallback...`);
                    this.src = `${basePath}.svg`;
                } else if (this.src && this.src.indexOf('.svg') !== -1 && this.src.indexOf('data:image/svg+xml') === -1) {
                    console.log(`Fallback to inline emoji for ${contextKey}`);
                    const encodedEmoji = encodeURIComponent(contextIcon || '🔍');
                    const bgColor = encodeURIComponent((contextColor || '#3b82f6') + '20'); // 20 hex is 12% opacity
                    this.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" style="background:${bgColor};"><text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" font-size="100">${encodedEmoji}</text></svg>`;
                } else {
                    // If all fails, hide image
                    console.log(`❌ No image found for ${contextKey}, hiding element.`);
                    this.style.display = 'none';
                    this.onerror = null; // Prevent infinite loops
                }
            };
        }

        // 5. Audio Integration (Text-to-Speech)
        if (audioHeader) {
            safeSetHTML(audioHeader, `
                <button id="btnAudioNarrative" class="btn-audio-narrative" data-context-color="${contextColor}" aria-label="Escuchar narración" style="background: white; border: 2px solid ${contextColor}; color: ${contextColor}; padding: 0.5rem 1rem; border-radius: 20px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 0.5rem;">
                    <span>🔊</span> Escuchar historia
                </button>
            `);

            const audioBtn = document.getElementById('btnAudioNarrative');
            if (audioBtn) {
                // ========================================
                // NARRATIVE AUDIO - STABILIZED
                // ========================================
                const handleToggle = () => {
                    if (window.toggleAudio) {
                        window.toggleAudio(audioBtn, narrativeText);
                    } else if (window.speak) {
                        window.speak(narrativeText, lang);
                    }
                };

                audioBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    handleToggle();
                });

                // Auto-Play Logic
                if (this.autoPlayTimer) clearTimeout(this.autoPlayTimer);
                this.autoPlayTimer = setTimeout(() => {
                    const narrativeScreen = document.getElementById('narrativeScreen');
                    if (narrativeScreen && narrativeScreen.classList.contains('active') && narrativeText && audioBtn && !audioBtn.classList.contains('playing')) {
                        console.log('📣 Auto-playing narrative intro...');
                        if (window.AudioManager) window.AudioManager.stop();
                        handleToggle();
                    }
                    this.autoPlayTimer = null;
                }, 1500);
            }
        }

        // 6. Wait for user to click "Start Adventure"
        // Return a promise that resolves when the button is clicked
        return new Promise(resolve => {
            if (startBtn) {
                // Focus for accessibility
                setTimeout(() => startBtn.focus(), 100);

                // Style button to match context
                startBtn.style.backgroundColor = contextColor;
                startBtn.style.borderColor = contextColor;

                // Define interaction
                startBtn.onclick = () => {
                    if (this.autoPlayTimer) {
                        clearTimeout(this.autoPlayTimer);
                        this.autoPlayTimer = null;
                    }
                    if (window.stopAudio) window.stopAudio();
                    else if (window.speechSynthesis) window.speechSynthesis.cancel();
                    resolve(true); // Resolves the promise -> SessionManager -> showProblemScreen
                };
            } else {
                console.warn('⚠️ Start button not found in Narrative Screen. Proceeding automatically.');
                setTimeout(() => resolve(true), 3000);
            }
        });
    },

    /**
     * Show narrative introduction as an on-demand modal overlay
     */
    showIntroModal(contextKey) {
        if (!contextKey && typeof window !== 'undefined' && window.store && window.store.config) {
            contextKey = window.store.config.context;
        }
        if (!contextKey) contextKey = 'cumpleanos';

        const lang = window.currentLanguage || 'es-ES';
        let narrativeText = "";
        if (window.CONTEXT_NARRATIVES && window.CONTEXT_NARRATIVES[contextKey]) {
            narrativeText = window.CONTEXT_NARRATIVES[contextKey][lang] || window.CONTEXT_NARRATIVES[contextKey]['es-ES'];
        } else {
            const local = this.getContextIntro(contextKey);
            narrativeText = local.intro;
        }

        let contextTitle = "";
        let contextIcon = "";
        let contextColor = "#3b82f6";

        if (window.CONTEXTS) {
            const ctx = window.CONTEXTS.find(c => c.id === contextKey);
            if (ctx) {
                contextTitle = ctx?.title?.[lang] || ctx?.title?.['es-ES'];
                contextIcon = ctx.icon;
                if (ctx.color) contextColor = ctx.color;
            }
        }
        if (!contextTitle) {
            const local = this.getContextIntro(contextKey);
            contextTitle = local.title;
            contextIcon = local.icon;
        }

        const basePath = `assets/img/contexts/intro_${contextKey}`;

        let modal = document.getElementById('contextIntroModalV2');
        if (!modal) {
            modal = document.createElement('div');
            modal.id = 'contextIntroModalV2';
            modal.style.cssText = `
                position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
                background-color: rgba(0, 0, 0, 0.8); z-index: 2147483645;
                display: flex; justify-content: center; align-items: center;
                padding: 1rem; box-sizing: border-box; backdrop-filter: blur(4px);
            `;
            document.body.appendChild(modal);
        }

        modal.innerHTML = `
            <div style="position: relative; background: #ffffff; width: 100%; max-width: 650px; max-height: 90vh; overflow-y: auto; border-radius: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.5); border: 4px solid ${contextColor}; padding: 1.5rem; display: flex; flex-direction: column; gap: 1rem;">
                <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 0.8rem;">
                    <h2 style="color: ${contextColor}; margin: 0; font-size: 1.8rem; display: flex; align-items: center; gap: 0.5rem; font-family: 'Baloo 2', cursive;">
                        <span>${contextIcon}</span>
                        <span>${contextTitle}</span>
                    </h2>
                    <button id="closeContextIntroModalBtn" style="background: #f1f5f9; border: none; border-radius: 50%; width: 40px; height: 40px; font-size: 1.2rem; cursor: pointer; color: #64748b;">✕</button>
                </div>

                <div style="text-align: center; margin: 0.5rem 0;">
                    <img id="modalContextImage" src="${basePath}.png" alt="${contextTitle}" style="max-height: 180px; width: auto; max-width: 100%; border-radius: 12px; border: 3px solid ${contextColor}; box-shadow: 0 4px 10px rgba(0,0,0,0.1); margin: 0 auto; display: block;" onerror="this.style.display='none';" />
                </div>

                <div style="background: #f8fafc; padding: 1.2rem; border-radius: 14px; border-left: 5px solid ${contextColor};">
                    <p style="font-size: 1.25rem; line-height: 1.6; color: #334155; font-family: 'Baloo 2', cursive; margin: 0; text-align: left;">
                        ${narrativeText}
                    </p>
                </div>

                <div style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-top: 0.5rem;">
                    <button id="btnAudioContextModal" class="btn" style="background: white; border: 2px solid ${contextColor}; color: ${contextColor}; padding: 0.6rem 1.2rem; border-radius: 20px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; font-size: 1.05rem;">
                        <span>🔊</span> Escuchar historia
                    </button>
                    <button id="btnDoneContextModal" class="btn" style="background: ${contextColor}; color: white; border: none; padding: 0.6rem 1.4rem; border-radius: 20px; font-weight: bold; cursor: pointer; font-size: 1.05rem;">
                        ✓ Volver al problema
                    </button>
                </div>
            </div>
        `;

        modal.style.display = 'flex';

        const closeModal = () => {
            modal.style.display = 'none';
            if (window.stopAudio) window.stopAudio();
            else if (window.speechSynthesis) window.speechSynthesis.cancel();
        };

        const closeBtn = document.getElementById('closeContextIntroModalBtn');
        const doneBtn = document.getElementById('btnDoneContextModal');
        if (closeBtn) closeBtn.onclick = closeModal;
        if (doneBtn) doneBtn.onclick = closeModal;

        modal.onclick = (e) => {
            if (e.target === modal) closeModal();
        };

        const audioBtn = document.getElementById('btnAudioContextModal');
        if (audioBtn) {
            audioBtn.onclick = (e) => {
                e.stopPropagation();
                if (window.toggleAudio) {
                    window.toggleAudio(audioBtn, narrativeText);
                } else if (window.speak) {
                    window.speak(narrativeText, lang);
                }
            };
        }
    }
};

// Exportar para uso global
if (typeof window !== 'undefined') {
    window.NarrativeManager = NarrativeManager;
}

export default NarrativeManager;
